"use client";

import { Card } from "@/components/ui/card";
import { Shield, Eye, Lock, Bell, Database, UserCheck, FileText, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";

export default function PrivacyPolicyPage() {
  const router = useRouter();
  const lastUpdated = "November 25, 2025";

  return (
    <div className="container max-w-3xl mx-auto p-4 pb-32 space-y-6">
      <div className="mb-4 flex items-center">
        <Button 
          variant="outline" 
          size="sm" 
          className="text-muted-foreground hover:text-foreground border-muted-foreground/30 hover:border-muted-foreground/50"
          onClick={() => router.back()}
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-center mb-2 text-primary">Privacy Policy</h1>
      <p className="text-center text-muted-foreground mb-8">Last Updated: {lastUpdated}</p>

      <div className="prose prose-sm max-w-none text-foreground">
        <p>
          At BatchFinder, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
        </p>
        <p>
          Please read this Privacy Policy carefully. By using BatchFinder, you consent to the data practices described in this policy. If you do not agree with this policy, please do not use our service.
        </p>
      </div>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Database className="text-primary" />
          <h2>1. Information We Collect</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p className="font-semibold text-foreground">
            Personal Information
          </p>
          <p>
            We collect personal information that you voluntarily provide to us when you:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><span className="font-semibold">Phone Number:</span> Required for account creation, verification, and SMS notifications</li>
            <li><span className="font-semibold">Location Data:</span> Your device's geolocation to show relevant batches in your area</li>
            <li><span className="font-semibold">Account Information:</span> Account ID, session information, and preferences</li>
            <li><span className="font-semibold">Instacart Credentials:</span> If you choose to connect your Instacart account for enhanced functionality</li>
            <li><span className="font-semibold">Payment Information:</span> Processed securely through third-party payment processors for subscription billing</li>
          </ul>

          <p className="font-semibold text-foreground mt-4">
            Automatically Collected Information
          </p>
          <p>
            When you use BatchFinder, we automatically collect certain information, including:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Device information (type, operating system, browser type)</li>
            <li>IP address and general location data</li>
            <li>Usage data (features used, time spent, interactions)</li>
            <li>Log data (access times, pages viewed, errors)</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Eye className="text-primary" />
          <h2>2. How We Use Your Information</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><span className="font-semibold">Provide Services:</span> Create and manage your account, authenticate users, and deliver core BatchFinder functionality</li>
            <li><span className="font-semibold">Send Notifications:</span> Send SMS messages with batch alerts, verification codes, and important account updates</li>
            <li><span className="font-semibold">Location-Based Services:</span> Show you relevant batches and stores based on your location</li>
            <li><span className="font-semibold">Process Payments:</span> Handle subscription billing and payment processing</li>
            <li><span className="font-semibold">Improve Services:</span> Analyze usage patterns to enhance features and user experience</li>
            <li><span className="font-semibold">Customer Support:</span> Respond to inquiries and provide technical assistance</li>
            <li><span className="font-semibold">Security:</span> Detect fraud, prevent abuse, and protect our users and service</li>
            <li><span className="font-semibold">Legal Compliance:</span> Comply with applicable laws and legal obligations</li>
          </ul>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Bell className="text-primary" />
          <h2>3. SMS and Text Messaging</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p className="font-semibold text-foreground">
            Collection and Use of Phone Numbers
          </p>
          <p>
            We collect your phone number only when you voluntarily provide it to us, for example, during account registration, transactions, inquiries, or when you sign up to receive notification messages. By providing your phone number and checking the SMS consent box, you expressly consent to receive automated text messages from BatchFinder.
          </p>

          <p className="font-semibold text-foreground mt-4">
            SMS Provider Sharing
          </p>
          <p>
            Your phone number is shared with our SMS service provider for the sole purpose of delivering text messages to you. Our SMS provider is contractually obligated to protect your information and may not use it for any other purpose.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Message Frequency and Charges
          </p>
          <p>
            Message frequency varies based on your account activity and settings. Message and data rates may apply based on your mobile carrier's plan. BatchFinder does not charge for messages, but your carrier may.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Opting Out of Messages
          </p>
          <p>
            If at any time you wish to stop receiving messages from us, you can opt out by:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Texting <span className="font-semibold">STOP</span> to any message from us</li>
            <li>Adjusting notification settings in your account</li>
            <li>Contacting us at avalon.batchfinder@gmail.com</li>
          </ul>
          <p>
            Once you have opted out, we will not send you any more SMS messages, except for critical transactional messages necessary for account security (such as verification codes). To stop all messages entirely, you may close your account.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Privacy of Phone Numbers
          </p>
          <p>
            We will never sell, rent, or transfer your phone number to any third party for their marketing purposes. Once you have opted out, we will not send you any more SMS messages, nor will we sell or transfer your phone number to another party. After opt-out, your phone number will be retained only as necessary for account management and legal compliance.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <UserCheck className="text-primary" />
          <h2>4. Information Sharing and Disclosure</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            We may share your information in the following circumstances:
          </p>
          
          <p className="font-semibold text-foreground mt-4">
            Service Providers
          </p>
          <p>
            We share information with third-party service providers who perform services on our behalf, including:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>SMS messaging providers (for text notifications)</li>
            <li>Payment processors (for subscription billing)</li>
            <li>Cloud hosting providers (for data storage and infrastructure)</li>
            <li>Analytics providers (for usage analysis and improvement)</li>
          </ul>
          <p>
            These service providers are contractually obligated to protect your information and use it only for the specific services they provide to us.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Legal Requirements
          </p>
          <p>
            We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas, government regulations).
          </p>

          <p className="font-semibold text-foreground mt-4">
            Business Transfers
          </p>
          <p>
            If BatchFinder is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you via email and/or prominent notice on our service before your information is transferred.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Aggregated Data
          </p>
          <p>
            We may share aggregated or de-identified information that cannot reasonably be used to identify you for research, marketing, or other purposes.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Lock className="text-primary" />
          <h2>5. Data Security</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication and session management</li>
            <li>Regular security assessments and monitoring</li>
            <li>Limited access to personal information on a need-to-know basis</li>
            <li>Secure payment processing through PCI-DSS compliant providers</li>
          </ul>
          <p className="mt-2">
            However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Shield className="text-primary" />
          <h2>6. Your Privacy Rights</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            Depending on your location, you may have certain rights regarding your personal information:
          </p>
          
          <p className="font-semibold text-foreground mt-4">
            Access and Portability
          </p>
          <p>
            You have the right to request access to the personal information we hold about you and receive a copy of that information in a portable format.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Correction
          </p>
          <p>
            You have the right to request correction of inaccurate or incomplete personal information.
          </p>

          <p className="font-semibold text-foreground mt-4">
            Deletion
          </p>
          <p>
            You have the right to request deletion of your personal information, subject to certain legal exceptions (e.g., legal obligations, fraud prevention).
          </p>

          <p className="font-semibold text-foreground mt-4">
            Opt-Out
          </p>
          <p>
            You have the right to opt out of:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>SMS notifications (text STOP or adjust settings)</li>
            <li>Promotional communications</li>
            <li>Certain data collection (e.g., location services via device settings)</li>
          </ul>

          <p className="font-semibold text-foreground mt-4">
            Exercising Your Rights
          </p>
          <p>
            To exercise any of these rights, please contact us at avalon.batchfinder@gmail.com. We will respond to your request within 30 days.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Database className="text-primary" />
          <h2>7. Data Retention</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Specific retention periods include:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><span className="font-semibold">Active Accounts:</span> Data retained while your account is active</li>
            <li><span className="font-semibold">Closed Accounts:</span> Most data deleted within 90 days, except as required for legal compliance</li>
            <li><span className="font-semibold">Payment Records:</span> Retained for 7 years for tax and accounting purposes</li>
            <li><span className="font-semibold">SMS Opt-Out Records:</span> Retained to honor your opt-out preferences</li>
          </ul>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <UserCheck className="text-primary" />
          <h2>8. Children's Privacy</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            BatchFinder is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete that information promptly.
          </p>
          <p>
            If you believe we have collected information from a child under 18, please contact us immediately at avalon.batchfinder@gmail.com.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <FileText className="text-primary" />
          <h2>9. Third-Party Links and Services</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            Our service may contain links to third-party websites, services, or applications (such as Instacart). This Privacy Policy does not apply to these third parties. We are not responsible for the privacy practices of these third parties.
          </p>
          <p>
            We encourage you to review the privacy policies of any third-party services before providing them with your personal information.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Bell className="text-primary" />
          <h2>10. Changes to This Privacy Policy</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            We may periodically update this Privacy Policy to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you about significant changes in the way we treat your information by:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Placing a prominent notice on our site or app</li>
            <li>Sending you an email notification (if you have provided an email address)</li>
            <li>Sending an SMS notification for significant changes affecting your rights</li>
          </ul>
          <p className="mt-2">
            Your continued use of BatchFinder after the effective date of the updated Privacy Policy constitutes your acceptance of the changes. We encourage you to review this Privacy Policy periodically.
          </p>
          <p className="mt-2">
            We thank you for your understanding and cooperation. If you have any questions or concerns about this Privacy Policy, please feel free to contact us at the address below.
          </p>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Mail className="text-primary" />
          <h2>11. Contact Us</h2>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
          </p>
          <div className="mt-3 p-4 bg-muted/50 rounded-lg">
            <p className="font-semibold text-foreground">Email:</p>
            <p className="font-medium text-primary">avalon.batchfinder@gmail.com</p>
          </div>
          <p className="mt-3">
            We will respond to your inquiry within 30 days.
          </p>
        </div>
      </Card>

      <div className="mt-8 text-center">
        <p className="text-muted-foreground text-sm">
          By using BatchFinder, you acknowledge that you have read, understood, and agree to this Privacy Policy. For information about your legal rights and obligations, please review our{" "}
          <Link href="/terms-of-service" className="text-primary hover:underline">
            Terms of Service
          </Link>.
        </p>
      </div>
    </div>
  );
}

